import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ReactLenis, useLenis } from "lenis/react";

// Touch devices scroll natively (Android Chrome + iOS). Lenis in root mode
// hijacks the html scroll container and breaks touch scrolling on some
// Android builds, so we skip smooth scroll on touch devices and let the
// browser handle it.
function isTouchDevice() {
  if (typeof window === "undefined") return false;
  return (
    "ontouchstart" in window ||
    (navigator.maxTouchPoints || 0) > 0 ||
    window.matchMedia?.("(hover: none) and (pointer: coarse)").matches
  );
}
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import FloatingContact from "@/components/FloatingContact";
import GeoGuard from "@/components/GeoGuard";
import Book from "@/pages/Book";
import Diagnosis from "@/pages/Diagnosis";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import FieldNotes from "@/pages/FieldNotes";
import FieldNote from "@/pages/FieldNote";
import DiagnosisPro from "@/pages/DiagnosisPro";
import DiagnosisProSuccess from "@/pages/DiagnosisProSuccess";
import DiagnosisProCancel from "@/pages/DiagnosisProCancel";
import Diagnose from "@/pages/Diagnose";
import TheDifference from "@/pages/TheDifference";
import Engine from "@/pages/Engine";
import Build from "@/pages/Build";
import GrowthPartnership from "@/pages/GrowthPartnership";
import StrategyBrief from "@/pages/StrategyBrief";
import Admin from "@/pages/Admin";
import InstallPrompt from "@/components/InstallPrompt";
import PwaBoot from "@/components/PwaBoot";
import RouteFade from "@/components/RouteFade";
import ContinueWhisper from "@/components/ContinueWhisper";
import MiniPlayer from "@/components/MiniPlayer";
import DiagnoseBubble from "@/components/DiagnoseBubble";
import CinematicChrome from "@/components/CinematicChrome";
import Canonical from "@/components/Canonical";
import PageMeta from "@/components/PageMeta";
import { AudioPlayerProvider } from "@/lib/audioPlayer";

function ScrollManager() {
  const location = useLocation();
  const lenis = useLenis();
  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [location.pathname, lenis]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const isDiagnosisPro = pathname.startsWith("/diagnosis-pro");
  const isAdmin = pathname.startsWith("/admin");
  const hideNav = isDiagnosisPro || isAdmin;
  const hideFooter = pathname === "/story" || isDiagnosisPro || isAdmin;
  const hideFloating = pathname === "/story" || isDiagnosisPro || isAdmin;
  return (
    <>
      <Canonical />
      <CinematicChrome />
      {!hideNav && <Nav />}
      <ScrollManager />
      <Routes>
        <Route path="/" element={<Engine />} />
        <Route path="/engine" element={<Engine />} />
        <Route path="/build" element={<Build />} />
        <Route path="/story" element={<Book />} />
        <Route path="/diagnose" element={<Diagnose />} />
        <Route path="/diagnosis" element={<Diagnosis />} />
        <Route path="/diagnosis-pro" element={<DiagnosisPro />} />
        <Route path="/diagnosis-pro/success" element={<DiagnosisProSuccess />} />
        <Route path="/diagnosis-pro/cancel" element={<DiagnosisProCancel />} />
        <Route path="/strategy-brief" element={<StrategyBrief />} />
        <Route path="/the-difference" element={<TheDifference />} />
        <Route path="/growth-partnership" element={<GrowthPartnership />} />
        <Route path="/field-notes" element={<FieldNotes />} />
        <Route path="/field-notes/:slug" element={<FieldNote />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/admin" element={<Admin />} />

        {/* Legacy redirects - preserve link equity for retired routes */}
        <Route path="/journey" element={<Navigate to="/" replace />} />
        <Route path="/the-founder" element={<Navigate to="/" replace />} />
        <Route path="/how-we-work" element={<Navigate to="/" replace />} />
        <Route path="/client-acquisition" element={<Navigate to="/engine" replace />} />
        <Route path="/pipeline" element={<Navigate to="/engine" replace />} />
        <Route path="/showreel" element={<Navigate to="/engine" replace />} />
        <Route path="/vs/:slug" element={<Navigate to="/the-difference" replace />} />
        <Route path="/answers" element={<Navigate to="/field-notes" replace />} />
        <Route path="/answers/:slug" element={<Navigate to="/field-notes" replace />} />

        {/* Retired SEO systems - all redirect to home (fresh SEO to be rebuilt) */}
        <Route path="/founder-hub" element={<Navigate to="/" replace />} />
        <Route path="/where-i-serve" element={<Navigate to="/" replace />} />
        <Route path="/for/letter/:slug" element={<Navigate to="/" replace />} />
        <Route path="/for/:slug" element={<Navigate to="/" replace />} />
        <Route path="/origin-graph" element={<Navigate to="/" replace />} />
        <Route path="/growth/:city/:vertical" element={<Navigate to="/" replace />} />
        <Route path="/method/personalised-video-outreach" element={<Navigate to="/" replace />} />
        <Route path="/linkedin-kit" element={<Navigate to="/" replace />} />
        <Route path="/grow" element={<Navigate to="/" replace />} />
        <Route path="/grow/problem" element={<Navigate to="/" replace />} />
        <Route path="/grow/solutions" element={<Navigate to="/" replace />} />
        <Route path="/grow/:city" element={<Navigate to="/" replace />} />
        <Route path="/ai-visibility/:slug" element={<Navigate to="/" replace />} />

        {/* Common alternate paths + typos - 301 to canonical pages */}
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/about" element={<Navigate to="/story" replace />} />
        <Route path="/the-book" element={<Navigate to="/story" replace />} />
        <Route path="/book" element={<Navigate to="/story" replace />} />
        <Route path="/blog" element={<Navigate to="/field-notes" replace />} />
        <Route path="/blog/:slug" element={<Navigate to="/field-notes" replace />} />
        <Route path="/insights" element={<Navigate to="/field-notes" replace />} />
        <Route path="/journal" element={<Navigate to="/field-notes" replace />} />
        <Route path="/newsletter" element={<Navigate to="/field-notes" replace />} />
        <Route path="/services" element={<Navigate to="/growth-partnership" replace />} />
        <Route path="/pricing" element={<Navigate to="/growth-partnership" replace />} />
        <Route path="/case-studies" element={<Navigate to="/the-difference" replace />} />
        <Route path="/work" element={<Navigate to="/the-difference" replace />} />
        <Route path="/contact" element={<Navigate to="/diagnose" replace />} />
        <Route path="/book-a-call" element={<Navigate to="/growth-partnership" replace />} />
        <Route path="/audit" element={<Navigate to="/diagnose" replace />} />
        <Route path="/free-audit" element={<Navigate to="/diagnosis" replace />} />

        {/* Catch-all: unknown routes 301 to home so no visitor lands on a blank SPA */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!hideFooter && <Footer />}
      {!hideFloating && <FloatingContact />}
      {!isDiagnosisPro && <InstallPrompt />}
      {!isDiagnosisPro && <ContinueWhisper />}
      <RouteFade />
      <PwaBoot />
      <MiniPlayer />
      {!isDiagnosisPro && <DiagnoseBubble />}
      {/* PageMeta must render LAST so its head-tag effects fire after
          per-page usePageTitle effects. This lets PageMeta act as the
          canonical single source of truth for <title> and meta tags. */}
      <PageMeta />
    </>
  );
}

function App() {
  const [touch, setTouch] = useState(false);
  useEffect(() => {
    setTouch(isTouchDevice());
  }, []);

  const inner = (
    <BrowserRouter>
      <GeoGuard>
        <AudioPlayerProvider>
          <Shell />
        </AudioPlayerProvider>
      </GeoGuard>
      <Toaster position="top-center" richColors closeButton />
    </BrowserRouter>
  );

  return (
    <div className="App grain">
      <HelmetProvider>
        {touch ? (
          inner
        ) : (
          <ReactLenis root options={{ lerp: 0.09, smoothWheel: true, wheelMultiplier: 1 }}>
            {inner}
          </ReactLenis>
        )}
      </HelmetProvider>
    </div>
  );
}

export default App;
